export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

const restrictedListeners = new Set<() => void>();
export function onRestricted(listener: () => void) { restrictedListeners.add(listener); return () => restrictedListeners.delete(listener); }

const unauthorizedListeners = new Set<(error: ApiError) => void>();

export function onUnauthorized(listener: (error: ApiError) => void) {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

async function apiResponse(path: string, init?: RequestInit) {
  const isFormData = init?.body instanceof FormData;
  const method = (init?.method ?? "GET").toUpperCase();
  const needsCsrf = !["GET", "HEAD", "OPTIONS"].includes(method)
    && (path.startsWith("/word-store/") || path === "/management" || path.startsWith("/management/") || path === "/admin" || path.startsWith("/admin/") || path === "/auth/password/change");
  let csrfToken: string | undefined;
  if (needsCsrf) {
    const csrfResponse = await fetch("/api/auth/csrf", { credentials: "include" });
    if (!csrfResponse.ok) {
      const body = await csrfResponse.json().catch(() => null) as { code?: string } | null;
      const error = new ApiError("Das Sicherheitstoken konnte nicht geladen werden.", csrfResponse.status, body?.code);
      if (csrfResponse.status === 401) unauthorizedListeners.forEach((listener) => listener(error));
      throw error;
    }
    csrfToken = ((await csrfResponse.json()) as { token: string }).token;
  }
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body && !isFormData ? { "Content-Type": "application/json" } : {}),
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string | string[]; code?: string } | null;
    const message = Array.isArray(body?.message) ? body.message.join(" ") : body?.message;
    const error = new ApiError(message || "Die Anfrage konnte nicht verarbeitet werden.", response.status, body?.code);
    if (error.code === "ORGANIZATION_RESTRICTED") restrictedListeners.forEach((listener) => listener());
    if (response.status === 401) unauthorizedListeners.forEach((listener) => listener(error));
    throw error;
  }
  return response;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiResponse(path, init);
  return response.json() as Promise<T>;
}

export async function apiBlob(path: string, init?: RequestInit) {
  const response = await apiResponse(path, init);
  return { blob: await response.blob(), response };
}
